import { generatePath } from 'react-router'
import recommend from '@algolia/recommend'
import type { RecordWithObjectID } from '@algolia/recommend-core'
import { useRelatedProducts } from '@algolia/recommend-react'
import { ProductHit } from '@scentregroup/shared/components/search/types'
import { getAlgoliaApiKeys } from '@scentregroup/shared/config'
import { displayStringForDiscountPercentageRange } from '@scentregroup/shared/helpers/percentage'
import {
  discountedPriceRange,
  discountPercentageRange,
  priceRange,
} from '@scentregroup/shared/helpers/price-ranges'
import { PRODUCT } from '@scentregroup/shared/components/product-card'
import { displayStringForPriceRange } from '@scentregroup/shared/helpers/product-price'
import { stringToSlug } from '@scentregroup/shared/helpers/slug'
import { SGPageTemplateProduct } from '../../../../../../../src/types/page-templates'
import { MarketplaceProduct } from '../../../types/marketplace-product'

const algoliaApiKeys = getAlgoliaApiKeys()

export const recommendClient = recommend(
  algoliaApiKeys.marketplaceApplicationId,
  algoliaApiKeys.marketplaceApiKey
)

export const indexName = 'products_au'

export type Recommendation = RecordWithObjectID<ProductHit>
export type Recommendations = Recommendation[]

interface IUseRecommendationsParams {
  variantGroupCode: string
  product: MarketplaceProduct
  shopId: string
  facetFilters: string[]
}

type UseRecommendations = (params: IUseRecommendationsParams) => {
  recommendations: SGPageTemplateProduct[]
  indexName: string
}

export const useRecommendations: UseRecommendations = ({
  variantGroupCode,
  product,
  shopId,
  facetFilters = [],
}) => {
  const activeSku = product.variants.find(v => v.quantity !== 0)?.sku
  const objectIDs = activeSku
    ? [
        `mirakl/${variantGroupCode}/${
          // only find variant with active offer to make sure this objectID exists in Algolia
          activeSku
        }/${shopId}`,
      ]
    : []

  const { recommendations } = useRelatedProducts<ProductHit>({
    recommendClient,
    indexName,
    objectIDs,
    queryParameters: {
      facetFilters: [
        'variant.active:true',
        `variantGroupCode:-${variantGroupCode}`,
        ...facetFilters,
      ],
    },
  })

  return { recommendations: recommendations.map(toProduct), indexName }
}

function toProduct(recommend: Recommendation): SGPageTemplateProduct {
  const {
    name,
    shop,
    image,
    brand,
    variant,
    variantGroupCode,
    category,
    objectID,
  } = recommend

  const discountPercentageRangeProduct = discountPercentageRange(recommend)

  return {
    title: name,
    price: displayStringForPriceRange(priceRange(recommend)),
    discountedPrice: displayStringForPriceRange(
      discountedPriceRange(recommend)
    ),
    percentDiscount: discountPercentageRangeProduct
      ? displayStringForDiscountPercentageRange(discountPercentageRangeProduct)
      : null,
    shop,
    brand: brand?.name ?? '',
    callToAction: {
      url: generatePath(`${PRODUCT}`, {
        productTitle: productTitle(brand, name),
        productId: variantGroupCode,
        shopId: `${shop.id}`,
      }),
    },
    image: {
      url: image ?? '',
      alt: name,
    },
    sku: variant.sku,
    variantGroupCode,
    category,
    objectID,
    __typename: 'SGPageTemplateProduct',
    kind: 'product',
  }
}

export function productTitle(
  brand: { name: string; slug: string } | undefined,
  name: string
): string | number | boolean {
  return brand?.name
    ? stringToSlug(`${brand.name}-${name}`)
    : stringToSlug(name)
}
